import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, Card, message } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, MessageOutlined } from '@ant-design/icons';
import { CHAT_OPEN_EVENT } from '@/components/ChatWidget';
import type { FormProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { storefrontApi, type ContactPageData } from '@/apis/storefrontApi';
import styles from './index.module.less';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

interface ContactFormValues {
  name: string;
  email: string;
  phone?: string;
  whatsapp?: string;
  company?: string;
  message: string;
}

const Contact: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [pageData, setPageData] = useState<ContactPageData | null>(null);

  useEffect(() => {
    storefrontApi
      .getContactPage()
      .then(setPageData)
      .catch(() => setPageData(null));
  }, [i18n.language]);

  const onFinish: FormProps<ContactFormValues>['onFinish'] = async (values) => {
    setSubmitting(true);
    try {
      await storefrontApi.submitContact({
        name: values.name,
        email: values.email,
        phone: values.phone,
        whatsapp: values.whatsapp,
        company: values.company,
        message: values.message,
      });
      message.success(t('contact.submitSuccess'));
    } catch (e) {
      message.error(t('contact.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const title = pageData?.title ?? 'Contact Us';
  const description = pageData?.description ?? 'Leave your contact info and message. We will get back to you as soon as possible.';
  const sidebarTitle = pageData?.sidebarTitle ?? 'Other ways to reach us';
  const contact = pageData?.contact ?? { email: 'support@example.com', phone: '+1 (000) 000-0000' };
  const sidebarNote = pageData?.sidebarNote ?? '';
  const whatsapp = contact.whatsapp;

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <Title level={2} className={styles.title}>
          {title}
        </Title>
        <Paragraph className={styles.desc}>{description}</Paragraph>

        <div className={styles.grid}>
          <Card className={styles.formCard}>
            <Form<ContactFormValues>
              layout="vertical"
              onFinish={onFinish}
              requiredMark={false}
            >
              <Form.Item
                name="name"
                label={t('contact.name')}
                rules={[{ required: true, message: t('contact.nameRequired') }]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder={t('contact.namePlaceholder')}
                  size="large"
                />
              </Form.Item>
              <Form.Item
                name="email"
                label={t('contact.email')}
                rules={[
                  { required: true, message: t('contact.emailRequired') },
                  { type: 'email', message: t('contact.emailInvalid') },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder={t('contact.emailPlaceholder')}
                  size="large"
                />
              </Form.Item>
              <Form.Item name="phone" label={t('contact.phone')}>
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="+1 (000) 000-0000"
                  size="large"
                />
              </Form.Item>
              <Form.Item name="whatsapp" label={t('contact.whatsapp')}>
                <Input
                  placeholder={t('contact.whatsappPlaceholder')}
                  size="large"
                />
              </Form.Item>
              <Form.Item name="company" label={t('contact.company')}>
                <Input placeholder={t('contact.companyPlaceholder')} size="large" />
              </Form.Item>
              <Form.Item
                name="message"
                label={t('contact.message')}
                rules={[{ required: true, message: t('contact.messageRequired') }]}
              >
                <TextArea
                  rows={4}
                  placeholder={t('contact.messagePlaceholder')}
                  size="large"
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={submitting}
                  block
                >
                  {t('contact.sendMessage')}
                </Button>
              </Form.Item>
            </Form>
          </Card>
          <div className={styles.sidebar}>
            <Card className={styles.chatCard}>
              <Title level={5}>{t('contact.liveChat')}</Title>
              <Paragraph className={styles.chatCardDesc}>
                {t('contact.liveChatDesc')}
              </Paragraph>
              <Button
                type="primary"
                icon={<MessageOutlined />}
                block
                size="large"
                onClick={() => window.dispatchEvent(new CustomEvent(CHAT_OPEN_EVENT))}
              >
                {t('chat.title')}
              </Button>
            </Card>
            <Card className={styles.infoCard}>
              <Title level={5}>{sidebarTitle}</Title>
              <Paragraph>
                <strong>Email:</strong> {contact.email}
              </Paragraph>
              <Paragraph>
                <strong>Phone:</strong> {contact.phone}
              </Paragraph>
              {whatsapp && (
                <Paragraph>
                  <strong>WhatsApp:</strong> {whatsapp}
                </Paragraph>
              )}
              {sidebarNote && (
                <Paragraph className={styles.sidebarNote}>{sidebarNote}</Paragraph>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
